import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from "react";

type Variant = "primary" | "secondary" | "ghost" | "outline" | "danger";
type Size = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  isLoading?: boolean;
  leadingIcon?: ReactNode;
}

const base =
  "inline-flex items-center justify-center gap-2 rounded-full font-body font-medium " +
  "transition-all duration-300 ease-premium focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-paper " +
  "disabled:cursor-not-allowed disabled:opacity-50";

const variants: Record<Variant, string> = {
  // Dorado con texto navy: es la combinación de mejor contraste dentro de la
  // identidad de marca (blanco sobre dorado no cumple AA). Ver design-system.md.
  primary:
    "bg-signal text-ink shadow-gold hover:-translate-y-0.5 hover:bg-signal-hover hover:shadow-glow-gold active:translate-y-0",
  secondary: "bg-ink text-paper hover:bg-navy-600 active:translate-y-px",
  outline: "border border-ink/15 text-ink hover:border-ink/30 hover:bg-ink/5",
  ghost: "bg-transparent text-ink hover:bg-ink/5 active:bg-ink/10",
  danger: "bg-red-600 text-white hover:bg-red-700 active:translate-y-px",
};

const sizes: Record<Size, string> = {
  sm: "h-9 px-4 text-sm",
  md: "h-11 px-5 text-base",
  lg: "h-12 px-7 text-lg",
};

/**
 * Botón base del sistema de diseño. Ver /docs/design-system.md.
 * `Guardar cambios`, no `Enviar` — el texto del botón siempre describe
 * la acción exacta que ejecuta.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { variant = "primary", size = "md", isLoading, leadingIcon, className = "", children, disabled, ...rest },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        className={`${base} ${variants[variant]} ${sizes[size]} ${className}`}
        disabled={disabled || isLoading}
        aria-busy={isLoading || undefined}
        {...rest}
      >
        {isLoading ? (
          <span
            className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
            aria-hidden="true"
          />
        ) : (
          leadingIcon
        )}
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";
